import React, {useEffect, useRef, useState} from "react";
import CollapsableBox from "../collapsableBox/CollapsableBox";
import useAppContext from "../../hooks/useAppContext";
import Achievements from "../whatsappBusinessAccount/onBoardingProcess/acheivements/Achievements";
import "./account-settings.css";
import {Button, Modal} from "antd";
import {disconnectFromWhatsappAccount} from "../../api/disconnectFromWhatsappAccount";
import useUserContext from "../../hooks/useUserContext";
import {releasePhoneNumber} from "../../api/ReleasePhoneNumber";
import useToast from "../../hooks/useToast";
import {ToastSkin} from "../../context/ToastContext";
import {useNavigate} from "react-router-dom";
import {isFreePricingPlanId} from "../../consts/freePricingPlans";

interface PhoneSectionProps {
    isInitiallyOpen?: boolean;
}

const AccountSection = ({ isInitiallyOpen = false }: PhoneSectionProps) => {
    const { data: { waba, phone, shop }, actions: { loadWaba }} = useAppContext();
    const { selectedDomain } = useUserContext();
    const [isDisconnectConfirmationOpen, setIsDisconnectConfirmationOpen] = useState(false);
    const [isReleasingPhoneConfirmationOpen, setIsReleasingPhoneConfirmationOpen] = useState(false);
    const wabaAccount = waba?.data?.waba;
    const hasPhoneId = !!phone?.whatsappPhoneId;
    const hasSenderId = !!wabaAccount?.providerSenderId;
    const isConnected = hasPhoneId && hasSenderId;
    const [isLoading, setIsLoading] = useState(false);
    const sectionRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const { showToast } = useToast();
    const isFreePlan = isFreePricingPlanId(shop?.data?.pricingPlan?.id);

    const toggleDisconnectConfirmation = () => {
        setIsDisconnectConfirmationOpen(!isDisconnectConfirmationOpen);
    }

    const toggleReleasePhoneNumberConfirmation = () => {
        const isByon = waba?.data?.waba?.isByon;
        if(isByon) {
            onConfirmReleaseOfPhoneNumber();
        } else {
            setIsReleasingPhoneConfirmationOpen(!isReleasingPhoneConfirmationOpen);
        }
    }

    const onConfirmDisconnect = async () => {
        setIsLoading(true);
        const response = selectedDomain && await disconnectFromWhatsappAccount(selectedDomain);
        if(response && response?.data?.integration && response.data?.sender) {
            toggleDisconnectConfirmation();
            loadWaba();
            setIsLoading(false);
            window.location.reload();
        } else {
            setIsLoading(false);
            showToast({ skin: ToastSkin.error, text: 'An error occurred while trying to disconnect your Whatsapp account. Please contact support if this issue persists.'});
        }
    }

    const onConfirmReleaseOfPhoneNumber = async () => {
        setIsLoading(true);
        const response = selectedDomain && await releasePhoneNumber(selectedDomain);
        if(response && response.data.phoneNumber) {
            toggleDisconnectConfirmation();
            loadWaba();
            setIsLoading(false);
            window.location.reload();
        } else {
            setIsLoading(false);
            setIsReleasingPhoneConfirmationOpen(false);
            showToast({ skin: ToastSkin.error, text: 'An error occurred while trying to release your phone number. Please contact support if this issue persists.'});
        }
    }

    useEffect(() => {
        if(isInitiallyOpen) {
            sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isInitiallyOpen]);

    const hasWaba = waba?.data?.waba?.providerSenderId;
    const hasPhone = waba?.data?.waba?.phoneId;

    return (
        <CollapsableBox title="Whatsapp Account & Phone Number" isInitiallyOpen={isInitiallyOpen}>
            <div ref={sectionRef} className="account-settings-container">
                {phone?.phoneNumber ? <Achievements showWhatsappAccountStatus={true} /> : null}
                <div className="account-actions">
                    {hasWaba ?
                        <Button type="default" size="large" className="danger-button"
                                onClick={toggleDisconnectConfirmation}>Disconnect Whatsapp account</Button>
                        : hasPhone && !isFreePlan ?
                            <Button type="default" size="large" className="danger-button"
                                    onClick={toggleReleasePhoneNumberConfirmation}>Release phone number</Button>
                            : null}
                    <Button type="default" size="large" onClick={() => window.open('https://app.vuelve.co/whatsapp-pricing')}>Messages Rates</Button>
                    {hasPhone && !hasWaba ? <Button type="primary" size="large"
                                                    onClick={() => navigate('/dashboard')}>Connect a Whatsapp Account</Button> : null}
                    {!hasPhone && !hasWaba ? <Button type="primary" size="large"
                                                    onClick={() => navigate('/dashboard')}>Get a Phone Number & Connect to Whatsapp</Button> : null}
                </div>
                <Modal open={isDisconnectConfirmationOpen} title="Disconnect Whatsapp account" onCancel={toggleDisconnectConfirmation} onOk={onConfirmDisconnect} confirmLoading={isLoading}>
                    <br />
                    <div>Disconnecting your Vuelve account from WhatsApp will remove the integration between your shop and your WhatsApp account. After this action, your WhatsApp phone number will still remain connected to Vuelve, but it will no longer be linked to your WhatsApp account.
                        If you choose to reconnect later, you’ll need to complete the WhatsApp integration process again — but you will not need to re-purchase your phone number.</div>
                    <br />
                    <div>Are you sure you want to disconnect?</div>
                </Modal>

                <Modal open={isReleasingPhoneConfirmationOpen} title="Release phone number" onCancel={toggleReleasePhoneNumberConfirmation} onOk={onConfirmReleaseOfPhoneNumber} confirmLoading={isLoading}>
                    <br />
                    <div>Releasing your phone number will permanently remove it from your Vuelve account. Once released, the number will no longer be available for use with WhatsApp or Vuelve.
                        If you wish to use WhatsApp with Vuelve again in the future, you will need to go through the full onboarding process, including setting up a new phone number and WhatsApp integration. This action will also require you to re-purchase a new phone number (incurring a new charge) before regaining the option to connect to your customers via WhatsApp and Vuelve.</div>
                    {shop?.data?.pricingPlan?.isPhoneNumberIncluded ?
                        <div>
                            <br />
                            ⚠️ Important: If your pricing plan includes a phone number, this only covers the initial registration of a single number. Registering a new number (even if the original number was released) will cost an additional $19 at the time of purchase. This extra cost applies only at registration and will not increase your monthly subscription fee moving forward.
                        </div>
                    : null }
                    <br />
                    <div>Are you sure you want to release your phone number?</div>
                </Modal>
            </div>
        </CollapsableBox>
    )
}

export default AccountSection;